import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ScenarioLockControllerApi, LockResponseStatusEnum } from '../api/generated';
import type { LockStatusResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { HTTP_CONFLICT, HTTP_OK } from '../constants/httpConstants';
import { buildPlanLockedMessage } from '../services/scenarioLockErrorService';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { useAppDispatch } from '../store/hooks';

const POLL_INTERVAL_MS = 10_000;
const RENEW_BUFFER_MS = 5_000;

// Identifies one lock. The async paths below can land after the scenario changed
// or the component went away, and every one of them has to be able to tell
// "the lock I started out with" from "whatever this hook points at now".
const lockKey = (locationId: string, scenarioId: string): string => `${locationId}:${scenarioId}`;

/**
 * Release for the paths that cannot await one: an effect cleanup, and an acquire
 * that landed after its instance was torn down.
 */
const releaseQuietly = (
  api: ScenarioLockControllerApi,
  locationId: string,
  scenarioId: string
): void => {
  void api.release({ locationId, scenarioId }).catch((error: unknown) => {
    // Nothing to recover to — the page or the scenario is already gone, and
    // there is no UI left to tell. Server-side expiry stays the backstop; the
    // log is for whoever hunts a plan that stayed locked for other users.
    console.error('Failed to release scenario lock', { locationId, scenarioId }, error);
  });
};

interface ScenarioLockResult {
  isLocked: boolean;
  lockedByCurrentUser: boolean;
  lockedByName: string | null;
  acquireLock: () => Promise<boolean>;
  releaseLock: () => Promise<void>;
}

/**
 * Owns this tab's scenario lock: the status poll, the acquire/release calls and
 * the renewal loop that keeps a held lock alive while the user edits.
 *
 * `onLockLost` fires when the renewal loop finds the lock gone — taken over, or
 * unreachable twice in a row. The hook cannot tear down the page's edit session
 * itself, so the consumer gets a one-shot callback to do it.
 */
export function useScenarioLock(
  locationId?: string,
  scenarioId?: string,
  onLockLost?: () => void
): ScenarioLockResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isLocked, setIsLocked] = useState(false);
  const [lockedByCurrentUser, setLockedByCurrentUser] = useState(false);
  const [lockedByName, setLockedByName] = useState<string | null>(null);
  const apiRef = useRef<ScenarioLockControllerApi | null>(null);
  const renewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Which lock this instance is currently set up for — null once torn down. The
  // async paths compare against it to notice they have been superseded.
  const activeLockKeyRef = useRef<string | null>(null);
  // Which lock this instance actually holds. An identity rather than a boolean:
  // an acquire that lands late would otherwise mark the *next* scenario as held
  // and get it released by a cleanup that never acquired it.
  //
  // `lockedByCurrentUser` is not a substitute either — the status poll also
  // reports it for a lock the same user holds in another tab.
  const heldLockKeyRef = useRef<string | null>(null);
  // The renewal timer closure is long-lived, so the callback goes through a ref:
  // captured directly it would keep calling the handler from the render that
  // scheduled the renewal, long after the page replaced it.
  const onLockLostRef = useRef(onLockLost);

  useEffect(() => {
    onLockLostRef.current = onLockLost;
  }, [onLockLost]);

  const clearRenewTimer = useCallback(() => {
    if (renewTimerRef.current) {
      clearTimeout(renewTimerRef.current);
      renewTimerRef.current = null;
    }
  }, []);

  /**
   * Refreshes the exposed lock state, and returns the holder name it resolved.
   *
   * The return value is what `acquireLock` reads. It cannot use `lockedByName`: a caller that
   * awaits this function is still inside the render that scheduled the state update, so the
   * closure it holds carries the *previous* holder — null, on exactly the path that matters
   * (the poll said unlocked, the user clicked Edit, and the acquire lost a race).
   */
  const updateStatus = useCallback(async (): Promise<string | null> => {
    if (!apiRef.current || !locationId || !scenarioId) return null;
    try {
      const response = await apiRef.current.getStatus({ locationId, scenarioId });
      // Whether the plan is locked is the presence of a lock, never the presence of a *name* for
      // its holder. The endpoint answers 204 with no body when nobody holds it, and 200 with the
      // lock otherwise — including when Keycloak cannot resolve the holder (a deleted user, a
      // stale row), where `lockedByName` comes back null over a perfectly live foreign lock.
      // Reading the name as "is it locked" left Edit enabled and the banner hidden in that case,
      // and the user found out only when the acquire came back 409.
      const body: unknown = response.data;
      const lock =
        response.status === HTTP_OK && typeof body === 'object' && body !== null
          ? (body as LockStatusResponse)
          : null;
      const lockedBy = lock?.lockedByName;
      const holderName = lockedBy
        ? `${lockedBy.firstName ?? ''} ${lockedBy.lastName ?? ''}`.trim() || null
        : null;
      setIsLocked(lock != null);
      setLockedByCurrentUser(lock?.lockedByCurrentUser ?? false);
      setLockedByName(holderName);
      return holderName;
    } catch (error: unknown) {
      // Transient poll failure — keep last-known state; the renew path decides
      // when the lock is really gone. Nothing to tell the user about a poll that
      // changed nothing on screen, but the log is the only breadcrumb for
      // whoever hunts a lock banner that stopped moving. The name still comes
      // back null: this call could not resolve a holder, and the caller's 409
      // message says the plan is locked without naming anyone.
      console.error('Failed to fetch scenario lock status', { locationId, scenarioId }, error);
      return null;
    }
  }, [locationId, scenarioId]);

  // The renewal loop re-arms itself from inside its own timer callback; the ref
  // carries the self-reference a `useCallback` cannot hold to itself.
  const scheduleRenewRef = useRef<((expiresAt: string) => void) | null>(null);

  const scheduleRenew = useCallback((expiresAt: string) => {
    clearRenewTimer();
    const expiresMs = new Date(expiresAt).getTime();
    const delay = Math.max(expiresMs - Date.now() - RENEW_BUFFER_MS, 1_000);
    renewTimerRef.current = setTimeout(() => {
      if (!apiRef.current || !locationId || !scenarioId) return;
      const key = lockKey(locationId, scenarioId);
      const api = apiRef.current;

      // The lock this tab was editing under is gone. Both callers have already
      // established that it is *this* lock that was lost, so the user is told
      // and the page is handed the teardown.
      const handleLockLost = (): void => {
        heldLockKeyRef.current = null;
        // All three, not just the held flag, and not left to the poll: the poll
        // is the very thing that may have just failed, and it now keeps
        // last-known state when it does. Half a reset would leave `isLocked`
        // true with no holder to name — a greyed-out Edit button beside a dialog
        // telling the user to start editing again. The successful poll below
        // fills in whoever holds it now.
        setIsLocked(false);
        setLockedByCurrentUser(false);
        setLockedByName(null);
        dispatch(showError({ message: t('lock-lost-error') }));
        onLockLostRef.current?.();
        // Who holds it now, so the toolbar can name them instead of offering Edit.
        void updateStatus();
      };

      const handleRenewal = (response: Awaited<ReturnType<typeof api.acquire>>): void => {
        const { status, expiresAt: newExpiry } = response.data;
        const isHeld =
          status === LockResponseStatusEnum.Acquired ||
          status === LockResponseStatusEnum.Renewed;

        // This renewal can land after we stopped holding the lock — the user
        // exited edit mode, the scenario changed, or the page went away, all
        // while the request was in flight. `clearRenewTimer` cannot help: the
        // timer already fired. Keyed on what is *held*, not on what the hook
        // is pointed at, because exiting edit mode leaves the latter unchanged.
        //
        // The renewal has just re-acquired the lock server-side, so hand it
        // back. Rescheduling instead would keep the plan locked for everyone
        // else for as long as this tab stays open, outliving the TTL.
        if (heldLockKeyRef.current !== key) {
          if (isHeld) releaseQuietly(api, locationId, scenarioId);
          return;
        }
        if (!isHeld) {
          // The lock is gone — expired, or taken over once it lapsed. Stop
          // claiming it, or the cleanup on the way out sends a release for a
          // lock this tab no longer has.
          handleLockLost();
          return;
        }
        if (newExpiry) scheduleRenewRef.current?.(newExpiry);
      };

      const handleRenewalFailure = (error: unknown): void => {
        // Renewal failed twice while the user is mid-edit, likely with unsaved
        // work: the lock lapses server-side so someone else can acquire it, so
        // it is treated as lost here too. Keeping edit mode open would only
        // invite a save that conflicts.
        console.error('Failed to renew scenario lock', { locationId, scenarioId }, error);
        // Only when it is still this lock we were holding: a rejection landing
        // after an explicit release, a scenario change or unmount is nobody's
        // problem, and must not raise a dialog over the next thing on screen.
        if (heldLockKeyRef.current === key) handleLockLost();
      };

      // The two-argument `.then` keeps the failure paths scoped to the requests
      // themselves — an error thrown inside `handleRenewal` must surface, not
      // vanish into a retry. The retry costs one request inside the renewal
      // buffer and saves the user's lock from a single transient blip.
      void api
        .acquire({ locationId, scenarioId })
        .then(handleRenewal, () =>
          api.acquire({ locationId, scenarioId }).then(handleRenewal, handleRenewalFailure)
        );
    }, delay);
  }, [clearRenewTimer, locationId, scenarioId, updateStatus, dispatch, t]);

  useEffect(() => {
    scheduleRenewRef.current = scheduleRenew;
  }, [scheduleRenew]);

  useEffect(() => {
    if (!locationId || !scenarioId) return;

    const key = lockKey(locationId, scenarioId);
    const api = new ScenarioLockControllerApi(createApiConfiguration());
    apiRef.current = api;
    activeLockKeyRef.current = key;

    void updateStatus();
    const interval = setInterval(() => { void updateStatus(); }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearRenewTimer();
      activeLockKeyRef.current = null;
      // Hand the lock back on the way out. Without this, navigating away or
      // switching scenario strands it until server-side expiry, which locks
      // every other user out of the plan in the meantime.
      //
      // Only this exact lock: a key mismatch means either nothing was acquired
      // or what is held belongs to a different scenario.
      if (heldLockKeyRef.current !== key) return;
      heldLockKeyRef.current = null;
      releaseQuietly(api, locationId, scenarioId);
    };
  }, [locationId, scenarioId, updateStatus, clearRenewTimer]);

  const acquireLock = useCallback(async (): Promise<boolean> => {
    if (!locationId || !scenarioId || !apiRef.current) return false;
    const key = lockKey(locationId, scenarioId);
    const api = apiRef.current;
    // Held by whom, as of right now — see updateStatus on why the return value and not the state.
    const holderName = await updateStatus();
    try {
      const response = await api.acquire({ locationId, scenarioId });
      const { status, expiresAt } = response.data;
      const acquired =
        status === LockResponseStatusEnum.Acquired ||
        status === LockResponseStatusEnum.Renewed;
      if (acquired) {
        // Two round-trips have gone by. If this instance was torn down or moved
        // to another scenario meanwhile — click Edit, then immediately follow a
        // nav link — the cleanup that would have released this lock has already
        // run and found nothing held. Hand it back here instead: holding it
        // would also start a renewal loop that outlives the page and keeps the
        // plan locked for everyone else indefinitely.
        if (activeLockKeyRef.current !== key) {
          releaseQuietly(api, locationId, scenarioId);
          return false;
        }
        heldLockKeyRef.current = key;
        setIsLocked(true);
        setLockedByCurrentUser(true);
        if (expiresAt) scheduleRenew(expiresAt);
      }
      return acquired;
    } catch (error) {
      console.error('Failed to acquire scenario lock:', error);
      // A clash comes back as a 409 whose body is the acquire endpoint's own LockResponse, where
      // `lockedBy` is a raw user UUID rather than a name — so the name has to come from the
      // status call above, which resolved it moments earlier for this very purpose.
      //
      // A 409 is proof the plan is locked, so it says so even when the status call could not name
      // the holder (a lock row whose user no longer exists in Keycloak). Only a non-409 failure
      // gets the generic "could not start editing", which is the one case where retrying is the
      // right advice.
      const isLockClash = axios.isAxiosError(error) && error.response?.status === HTTP_CONFLICT;
      dispatch(
        showError({
          message: isLockClash
            ? buildPlanLockedMessage(holderName, t)
            : t('lock-acquire-error')
        })
      );
      return false;
    }
  }, [locationId, scenarioId, updateStatus, scheduleRenew, dispatch, t]);

  const releaseLock = useCallback(async () => {
    if (!locationId || !scenarioId || !apiRef.current) return;
    clearRenewTimer();
    const key = lockKey(locationId, scenarioId);
    try {
      // Only a lock this instance actually holds. `lockedByCurrentUser` is not
      // license enough: the status poll also reports it for a lock the same
      // user holds in another tab, and that tab's lock is not ours to hand
      // back — releasing it would strip an ongoing edit session of its lock.
      if (heldLockKeyRef.current !== key) return;
      await apiRef.current.release({ locationId, scenarioId });
      // Forgotten only once the server has actually taken it back. On failure
      // the key stays, so the cleanup on unmount gets one more attempt.
      heldLockKeyRef.current = null;
    } catch (error) {
      // Callers are exiting edit mode either way, and there is nothing useful to
      // say to the user about a lock they are done with. The log is for whoever
      // hunts a plan that stayed locked for other users until expiry.
      console.error('Failed to release scenario lock', { locationId, scenarioId }, error);
    } finally {
      // Reset regardless of outcome: leaving these set shows the user as the
      // lock holder over a lock that is gone, or on its way out via expiry.
      // When nothing was held, the next status poll restores the true state.
      setIsLocked(false);
      setLockedByCurrentUser(false);
      setLockedByName(null);
    }
  }, [locationId, scenarioId, clearRenewTimer]);

  return { isLocked, lockedByCurrentUser, lockedByName, acquireLock, releaseLock };
}
